import { replace } from 'react-router-redux';
import { setWindowLocation } from '@eb/http';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';

import {
    trackSignInConfirmationAcknowledged,
    trackSignInConfirmationRejected,
} from './gaTracking';

import { ONBOARDING_REDIRECT_URL_BLACKLIST } from '../constants';

import { GA_ALL_SOCIALS, GA_EVENTBRITE } from '../constants/analytics';

/**
 * Returns an object out of the Login Data recieved
 * @param {object} arguments Login Data
 * @param {function} getState Function to be able to get the current state.
 * @param {boolean} newApiCall Signals if it was a call from the new api
 * @returns
 */
export const getLogInFormData = (
    {
        email: formEmail,
        password,
        captcha: recaptchaResponse,
        recaptchaV3ResponseToken,
    },
    getState,
) => {
    const {
        authentication: { email, forward, pckg, stld },
    } = getState();

    let {
        authentication: { referrer },
    } = getState();

    if (global.window && window.localStorage) {
        referrer = localStorage.getItem('referrer');
    }

    const apiResult = {
        password,
        email_address: formEmail || email,
        referrer,
        stld,
        recaptchaResponse,
        recaptchaV3ResponseToken,
    };

    return apiResult;
};

/**
 * Returns an object out of the Sign up Data recieved
 * @param {object} arguments Sign Up Data
 * @param {function} getState Function to be able to get the current state.
 * @returns
 */
export const getSignUpFormData = (
    {
        firstName,
        lastName,
        password,
        captcha: recaptchaResponse,
        email: formEmail,
    },
    getState,
) => {
    const {
        authentication: { email, forward, referrer, userTypeParams, pckg },
        status: { tosSocialAccepted },
    } = getState();

    const _email = formEmail || email;

    const signupData = {
        password,
        firstName,
        lastName,
        recaptchaResponse,
        email: _email,
        forward,
        referrer,
        ...userTypeParams,
        pckg,
    };
    // Only add this extra parameter when user accept ToS by Popup in new design.
    if (tosSocialAccepted) {
        signupData['accepted_tos_by_popup'] = 1;
    }

    return signupData;
};

/**
 * Returns an object out of the Social Login Data recieved
 * @param {object} arguments Social Login Data
 * @param {function} getState Function to be able to get the current state.
 * @returns
 */
export const getSocialLogInFormData = (
    { accessToken, sourceProvider, skipCreation },
    getState,
) => {
    const {
        authentication: { forward, pckg },
        status: { tosSocialAccepted },
    } = getState();

    let {
        authentication: { referrer },
    } = getState();

    // We need to remove the referrer from the local storage, so it won't cause issues on future logins.
    if (global.window && window.localStorage) {
        referrer = localStorage.getItem('referrer');
        localStorage.removeItem('referrer');
    }

    const socialData = {
        accessToken,
        sourceProvider,
        skipCreation,
        forward,
        referrer,
        pckg,
    };
    // Only add this extra parameter when user accept ToS by Popup in new design.
    if (tosSocialAccepted) {
        socialData['accepted_tos_by_popup'] = 1;
    }
    return socialData;
};

/**
 * Returns an object out of the Social Signup Data
 * @param {object} arguments  Social Signup Data
 * @param {object} currentState Contains the current State
 * @returns
 */
export const getSocialSignUpFormData = (
    { email, firstName, lastName },
    currentState,
) => {
    const skipCreation = false;
    const {
        authentication: { forward, referrer, pckg },
        social: { socialData, socialProvider: sourceProvider },
    } = currentState;

    const accessToken = socialData[sourceProvider]?.accessToken;

    return {
        accessToken,
        sourceProvider,
        skipCreation,
        email,
        firstName,
        lastName,
        forward,
        referrer,
        pckg,
    };
};

export const shouldOnboardingRedirect = (state) => {
    const referrer = state.authentication.referrer;

    return !find(ONBOARDING_REDIRECT_URL_BLACKLIST, (blacklistedPath) =>
        referrer.includes(blacklistedPath),
    );
};

export const getSocialConnectionsKey = (connectedSocials) => {
    let key = '';

    GA_ALL_SOCIALS.forEach((social) => {
        if (
            Array.isArray(connectedSocials) &&
            connectedSocials?.includes(social)
        ) {
            key = key + social;
        }
    });

    return key;
};

// Signin Confirmation Actions
export const acknowledgeMessage = () => (dispatch, getState) => {
    const state = getState();
    trackSignInConfirmationAcknowledged({ state });
    dispatch(replace('/signin'));
};

export const rejectMessage = (forwardURL) => (dispatch, getState) => {
    const state = getState();
    trackSignInConfirmationRejected({ state });
    setWindowLocation(forwardURL);
};

export const getQuerySourceProvider = ({ state }) => {
    let {
        social: { socialProvider: sourceProvider = '' },
    } = state;
    const query = state.routing.locationBeforeTransitions
        ? state.routing.locationBeforeTransitions.query
        : '';

    if (query['external_provider'] && query['external_provider'] != 'None') {
        sourceProvider = query['external_provider'];
    } else if (isEmpty(sourceProvider)) {
        sourceProvider = GA_EVENTBRITE;
    }
    return sourceProvider;
};
